import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Center,
    Collapse,
    Grid,
    HStack,
    IconButton,
    Input,
    Select,
    Text,
    useBreakpointValue,
    VStack
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { fetchAllSchedulesForWeek } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import TitleHeader from '../../components/TitleHeader';
import { useTableColors } from '../../Utils';

const Schedules = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [grouped, setGrouped] = useState({});
    const [startOfWeek, setStartOfWeek] = useState(dayjs().startOf('week').add(1, 'day'));
    const [courierFilter, setCourierFilter] = useState('');
    const [routeFilter, setRouteFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [allSchedules, setAllSchedules] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const {
        dayBoxBg,
        dayBoxHighlightBg,
        dayBoxDateColor,
        entryBoxBg,
        entryTimeColor,
        emptyTextColor,
    } = useTableColors();

    const columns = useBreakpointValue({ base: 1, md: 2, lg: 4, xl: 7 });
    const weekRef = useRef();
    const dayRefs = useRef({});

    useEffect(() => {
        const fetchSchedules = async () => {
            setLoading(true);
            try {
                const response = await fetchAllSchedulesForWeek(startOfWeek.format('YYYY-MM-DD'));
                if (response.success) {
                    setAllSchedules(response.data);
                } else {
                    setError('Failed to load schedules');
                }
            } catch (err) {
                console.error(err);
                setError('Failed to load schedules');
            }
            setLoading(false);
        };
        fetchSchedules();
    }, [startOfWeek]);

    useEffect(() => {
        const days = {};
        for (let i = 0; i < 7; i++) {
            const date = startOfWeek.clone().add(i, 'day').format('YYYY-MM-DD');
            days[date] = [];
        }

        let filtered = allSchedules;
        if (courierFilter) {
            filtered = filtered.filter(s => s.courierName === courierFilter);
        }
        if (routeFilter) {
            filtered = filtered.filter(s => s.routeName === routeFilter);
        }

        filtered.forEach(schedule => {
            const scheduleDate = dayjs(schedule.date).format('YYYY-MM-DD');
            if (days[scheduleDate]) {
                days[scheduleDate].push(schedule);
            }
        });

        setGrouped(days);
    }, [allSchedules, courierFilter, routeFilter, dateFilter, startOfWeek]);


    const uniqueCouriers = [...new Set(allSchedules.map(s => s.courierName))];
    const uniqueRoutes = [...new Set(allSchedules.map(s => s.routeName))];

    const resetToTodayWeek = () => {
        setStartOfWeek(dayjs().startOf('week').add(1, 'day'));
        setCourierFilter('');
        setRouteFilter('');
        setDateFilter('');
    };

    // const handleDateFilterChange = (selected) => {
    //     setDateFilter(selected);
    //     const dateObj = dayjs(selected);
    //     const newWeekStart = dateObj.day() === 0
    //         ? dateObj.subtract(6, 'day')
    //         : dateObj.startOf('week').add(1, 'day');
    //     setStartOfWeek(newWeekStart);
    // };



    const handleDateFilterChange = async (selected) => {
        const dateObj = dayjs(selected);
        const newWeekStart = dateObj.day() === 0
            ? dateObj.subtract(6, 'day')
            : dateObj.startOf('week').add(1, 'day');

        setDateFilter(selected);
        setStartOfWeek(newWeekStart);

        setLoading(true);
        try {
            const response = await fetchAllSchedulesForWeek(newWeekStart.format('YYYY-MM-DD'));

            console.log("Response", { response })
            console.log("selected: ", { selected })
            console.log("newWeekStart", { newWeekStart })


            if (response.success) {
                setAllSchedules(response.data); // Load new week's data
            } else {
                setError('Failed to load schedules');
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load schedules');
        }
        setLoading(false);
    };

    const handlePrintDay = (date) => {
        const entries = grouped[date] || [];
        const content = `\n      <html>\n        <head>\n          <title>${date}</title>\n          <style>\n            body { font-family: sans-serif; padding: 20px; }\n            table { width: 100%; border-collapse: collapse; margin-top: 10px; }\n            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }\n            th { background-color: #f0f0f0; }\n          </style>\n        </head>\n        <body>\n          <h2>Schedule for ${dayjs(date).format('dddd, MMMM D')}</h2>\n          <table>\n            <thead>\n              <tr><th>Courier</th><th>Route</th><th>Start Time</th></tr>\n            </thead>\n            <tbody>\n              ${entries.map(entry => `<tr><td>${entry.courierName}</td><td>${entry.routeName}</td><td>${entry.startTime}</td></tr>`).join('')}\n            </tbody>\n          </table>\n        </body>\n      </html>`;
        const win = window.open('', '_blank');
        win.document.write(content);
        win.document.close();
        win.print();
    };

    const handlePrintWeek = () => {
        const content = `\n      <html>\n        <head>\n          <title>Weekly Schedule</title>\n          <style>\n            body { font-family: sans-serif; padding: 20px; }\n            table { width: 100%; border-collapse: collapse; margin-top: 10px; }\n            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }\n            th { background-color: #f0f0f0; }\n          </style>\n        </head>\n        <body>\n          <h1>Weekly Schedule (${startOfWeek.format('MMM D')})</h1>\n          ${Object.entries(grouped).map(([date, entries]) => `\n            <h3>${dayjs(date).format('dddd, MMM D')}</h3>\n            <table>\n              <thead>\n                <tr><th>Courier</th><th>Route</th><th>Start Time</th></tr>\n              </thead>\n              <tbody>\n                ${entries.map(entry => `<tr><td>${entry.courierName}</td><td>${entry.routeName}</td><td>${entry.startTime}</td></tr>`).join('')}\n              </tbody>\n            </table>\n          `).join('')}\n        </body>\n      </html>`;
        const win = window.open('', '_blank');
        win.document.write(content);
        win.document.close();
        win.print();
    };

    if (loading) {
        return <Center mt="20%"><Loading /></Center>;
    }

    return (
        <Box maxW="full" mx="auto" p="8">
            <VStack spacing={6} align="stretch">
                {!error ? (
                    <>
                        <TitleHeader title="Couriers Schedules" />

                        <HStack justifyContent="space-between" mb={2}>
                            <Button onClick={() => setStartOfWeek(prev => prev.subtract(7, "day"))}>
                                ← Previous Week
                            </Button>
                            <HStack>
                                <Text fontWeight="bold">
                                    Week of {startOfWeek.format("MMMM D, YYYY")}{" "}
                                </Text>
                                <Button size="sm" onClick={resetToTodayWeek}>
                                    Back to Today
                                </Button>
                            </HStack>
                            <Button onClick={() => setStartOfWeek(prev => prev.add(7, "day"))}>
                                Next Week →
                            </Button>
                        </HStack>

                        <HStack justifyContent="space-between" mb={4}>
                            <HStack>
                                <IconButton
                                    icon={showFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    onClick={() => setShowFilters(!showFilters)}
                                    aria-label="Toggle Filters"
                                    size="sm"
                                />
                                <Text fontSize="sm">Filters</Text>
                            </HStack>
                            <Button size="sm" onClick={handlePrintWeek}>
                                Print Whole Week
                            </Button>
                        </HStack>

                        <Collapse in={showFilters} animateOpacity>
                            <HStack spacing={3} mb={4} wrap="wrap">
                                <Select
                                    placeholder="Filter by Courier"
                                    value={courierFilter}
                                    onChange={(e) => setCourierFilter(e.target.value)}
                                    maxW="200px"
                                >
                                    {uniqueCouriers.map((courier, idx) => (
                                        <option key={idx} value={courier}>
                                            {courier}
                                        </option>
                                    ))}
                                </Select>
                                <Select
                                    placeholder="Filter by Route"
                                    value={routeFilter}
                                    onChange={(e) => setRouteFilter(e.target.value)}
                                    maxW="200px"
                                >
                                    {uniqueRoutes.map((route, idx) => (
                                        <option key={idx} value={route}>
                                            {route}
                                        </option>
                                    ))}
                                </Select>
                                <Input
                                    type="date"
                                    value={dateFilter}
                                    onChange={(e) => handleDateFilterChange(e.target.value)}
                                    maxW="200px"
                                />
                                <Button size="sm" onClick={resetToTodayWeek}>
                                    Clear
                                </Button>
                            </HStack>
                        </Collapse>

                        <Grid templateColumns={`repeat(${columns}, 1fr)`} gap={4} ref={weekRef}>
                            {Object.entries(grouped).map(([date, entries], index) => (
                                <Box
                                    key={index}
                                    p={4}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    minH="180px"
                                    bg={
                                        dateFilter && dayjs(date).isSame(dateFilter, "day")
                                            ? dayBoxHighlightBg
                                            : dayBoxBg
                                    }
                                    boxShadow="sm"
                                    ref={(el) => (dayRefs.current[date] = el)}
                                >
                                    <HStack justifyContent="space-between" mb={2}>
                                        <Box>
                                            <Text fontWeight="bold">
                                                {dayjs(date).format("dddd")}
                                                {dayjs(date).isSame(dayjs(), "day") && (
                                                    <Badge ml={2} colorScheme="green">
                                                        Today
                                                    </Badge>
                                                )}
                                            </Text>
                                            <Text fontSize="sm" color={dayBoxDateColor}>
                                                {dayjs(date).format("MMM D")}
                                            </Text>
                                        </Box>
                                        <Button size="xs" onClick={() => handlePrintDay(date)}>
                                            Print
                                        </Button>
                                    </HStack>

                                    {entries.length > 0 ? (
                                        entries.map((entry, i) => (
                                            <Box
                                                key={i}
                                                mb={2}
                                                p={2}
                                                borderRadius="md"
                                                bg={entryBoxBg}
                                                boxShadow="sm"
                                                borderLeft="4px solid"
                                                borderColor="purple.400"
                                            >
                                                <HStack spacing={3} align="center">
                                                    <Avatar name={entry.courierName} size="sm" />
                                                    <Box>
                                                        <Text fontWeight="semibold">{entry.courierName}</Text>
                                                        <Badge colorScheme="purple" mr={2}>
                                                            {entry.routeName}
                                                        </Badge>
                                                        <Text fontSize="xs" color={entryTimeColor}>
                                                            {entry.startTime}
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        ))
                                    ) : (
                                        <Text fontSize="sm" color={emptyTextColor}>
                                            No schedules
                                        </Text>
                                    )}
                                </Box>
                            ))}
                        </Grid>
                    </>

                ) : (
                    <AccessDenied msg={error} />
                )}
            </VStack>
        </Box>
    );
};

export default Schedules;
